// Homepage featured tools band — G02 featured + published preview cards.
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionBadge from "@/components/site/SectionBadge";
import { track } from "@/lib/analytics";
import { publishedBySlug } from "@/data/tools/registry";

const FEATURED = "growth-system-scorecard";
const PREVIEWS = ["hidden-revenue-finder", "profit-leakage-calculator", "sales-productivity-calculator"];

export default function HomeTools() {
  const featured = publishedBySlug(FEATURED);
  const previews = PREVIEWS.map(publishedBySlug).filter(Boolean).filter((t) => t.id !== featured?.id);
  if (!featured) return null;

  return (
    <section data-testid="home-tools" className="py-16 md:py-24 bg-[#050505] text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionBadge label="Free Growth Tools" tone="light" />
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] mt-6 max-w-2xl">
              Diagnose it yourself, free. Then decide if you need us.
            </h2>
          </div>
          <Link
            to="/tools"
            data-testid="home-tools-all"
            onClick={() => track("tools_view_all_click", { source: "home" })}
            className="group inline-flex items-center gap-2 text-sm font-medium underline underline-offset-8 hover:text-zinc-300 transition-colors"
          >
            Explore All Growth Tools
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Featured: G02 */}
          <Link
            to={featured.url}
            data-testid="home-tools-featured"
            onClick={() => track("tool_card_click", { tool: featured.id, source: "home" })}
            className="group border border-white/15 p-8 md:p-10 flex flex-col hover:border-white/50 transition-colors duration-300"
          >
            <span className="text-[11px] font-semibold tracking-[0.28em] uppercase text-zinc-400">Featured assessment</span>
            <div className="mt-4 font-serif-display text-3xl md:text-4xl leading-tight">{featured.name}</div>
            <p className="mt-4 text-[15px] text-zinc-400 leading-relaxed flex-1">{featured.hook} Nine dimensions, ten minutes, three priorities — with a radar your leadership team can argue with.</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-medium">
              Score your growth system
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </span>
          </Link>

          {/* Preview cards */}
          <div className="grid gap-4">
            {previews.map((t) => (
              <Link
                key={t.id}
                to={t.url}
                data-testid={`home-tools-preview-${t.id}`}
                onClick={() => track("tool_card_click", { tool: t.id, source: "home" })}
                className="group border border-white/15 p-6 flex items-center justify-between gap-6 hover:border-white/50 transition-colors duration-300"
              >
                <div>
                  <span className="text-[10px] font-semibold tracking-[0.28em] uppercase text-zinc-500">{t.id}</span>
                  <div className="mt-1.5 font-serif-display text-xl leading-snug">{t.name}</div>
                  <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">{t.hook}</p>
                </div>
                <ArrowUpRight className="h-5 w-5 shrink-0 text-zinc-500 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
